import { landmineNames } from '@mm/constants/landmine-names';
import chatbotCRUDActions from '@mm/state/actions/ChatbotCRUDActions';
import { TemplatesCRUDActions } from '@mm/state/actions/TemplatesCRUDActions';
import { BasicChatbot } from '@mm/state/interfaces/chatbot';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import { TemplateModel } from '@mm/state/model/TemplateModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPagePlanInfoSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Field, FieldProps, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import * as Yup from 'yup';
import Button from '../../../../../common/buttons/DeprecatedButton/DeprecatedButton';
import { CHATBOT_HOME } from '../../../../../common/constants/routes';
import { RouterComponentProps } from '../../../../../common/helpers/routes/interfaces';
import { timeout } from '../../../../../common/helpers/utils';
import {
  BOT_NAME_LENGTH_ERROR,
  MAX_BOT_NAME_LENGTH,
} from '../../../../../editor/features/configure/panels/sections/BotSettingsSection/BotName/BotName';
import { CreateOrExtendProgressBar } from '../CreateBotOrExtendByTemplate/CreateOrExtendProgressBar';
import { TemplateFormLabel } from '../TemplateFormLabel/TemplateFormLabel';
import { TemplateModal } from '../TemplateModal/TemplateModal';
import { TemplateUploadFile } from '../TemplateUploadFile/TemplateUploadFile';
import { TrimInputSpaces } from '../TrimInputSpaces/TrimInputSpaces';
import './CreateTemplateFromBot.scss';

const templateNameSchema = Yup.string()
  .trim()
  .max(MAX_BOT_NAME_LENGTH, BOT_NAME_LENGTH_ERROR)
  .required('Template Name Required');

const templateBotIdSchema = Yup.number().required('Template Bot Required');

export const validationSchemaCreate = Yup.object().shape({
  name: templateNameSchema,
  botId: templateBotIdSchema,
});

export const validationSchemaEdit = Yup.object().shape({
  name: templateNameSchema,
});

type OuterProps = {
  templateId?: number;
  onClose: () => void;
  onCreatedTemplate?: (createdTemplate: TemplateModel) => void;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  RouterComponentProps;

type Props = OuterProps & InnerProps & RouteComponentProps;

type FormValues = {
  name: string;
  file?: string;
  description: string;
  botId?: number;
};

const CreateTemplateFromBotPure = ({
  templateId,
  company,
  bots,
  retrieveBasicListByCompany,
  onClose,
  onCreatedTemplate,
  getTemplateById,
  createTemplate,
  updateTemplate,
  isStillCloning,
  freeOrSpecialPricePlan,
}: Props) => {
  // cause formik issue, doesn't allow you lazy set initial value
  const [isProcessing, setIsProcessing] = useState(false);
  const [isShowingCloneModal, setIsShowingCloneModal] = useState(false);
  const [template, setTemplate] = useState({} as TemplateModel);
  const modalActions = useModalActions();

  const pollTillCloned = async (newTemplateId: number) => {
    const MAX_WAIT_TIME = 2 * 60 * 1000; // 2 minutes
    const POLL_INTERVAL = 3000;
    const pollMaxAttempts = MAX_WAIT_TIME / POLL_INTERVAL;
    let pollAttempts = 0;

    while (pollAttempts < pollMaxAttempts) {
      /* eslint-disable no-await-in-loop */
      await timeout(POLL_INTERVAL);
      const stillCloning = await isStillCloning(newTemplateId);
      /* eslint-enable no-await-in-loop */
      if (!stillCloning) return true;
      pollAttempts += 1;
    }

    throw new Error('Template creation timed out');
  };

  const submitForm = async (values: FormValues) => {
    setIsProcessing(true);

    if (freeOrSpecialPricePlan) {
      onClose();
      return modalActions.openUniversalUpgradeLandmineModal({
        landmineName:
          landmineNames[
            'TEMPLATES__CREATE-OR-EXTEND__CREATE-TEMPLATE-FROM-BOT'
          ],
        planFallback: PageBillingType.Pro,
      });
    }

    // templateId should be bot to clone
    if (templateId) {
      await updateTemplate(
        templateId,
        values.name,
        values.description,
        values.file || template.assetId,
      );
    } else {
      if (values.botId) {
        try {
          setIsShowingCloneModal(true);
          const maybeNewTemplate = await createTemplate(
            values.botId,
            values.name,
            values.description,
            values.file,
            true,
          );

          if (maybeNewTemplate && maybeNewTemplate.id) {
            onCreatedTemplate?.(maybeNewTemplate);
            await pollTillCloned(maybeNewTemplate.id);
          }
        } catch (err) {
          setIsShowingCloneModal(false);
          throw err;
        }
      }
    }

    setIsProcessing(false);
    onClose();
  };

  const getTemplateAsync = async (templateId: number) => {
    const t = await getTemplateById(templateId);
    if (t) {
      setTemplate(t);
    }
  };

  useEffect(() => {
    if (templateId) {
      if (company) {
        retrieveBasicListByCompany(company.id);
      }
      getTemplateAsync(templateId);
    }
  }, [templateId]);

  return (
    <TemplateModal isOpen={true}>
      <TemplateModal.Header toggle={onClose}>
        {templateId ? 'Update the ' : 'Create a new '} template
        <TemplateModal.SubHeader>
          Creating a Template will take a snapshot of your Chatbot as it exists
          right now. Any updates made to your Chatbot later will not be
          automatically included in your Template. Give your Template a
          memorable name and description. In the future, you'll have the option
          to share these Templates with the community, so you might want to give
          it a nice Cover Image too!
        </TemplateModal.SubHeader>
      </TemplateModal.Header>
      {!isShowingCloneModal ? (
        <TemplateModal.Body>
          <Formik
            initialValues={template}
            enableReinitialize
            validationSchema={
              !templateId ? validationSchemaCreate : validationSchemaEdit
            }
            onSubmit={submitForm}
          >
            {({ errors }) => (
              <fieldset disabled={isProcessing}>
                <Form>
                  <div className="template-row">
                    <div className="template-col template-col--preview">
                      <TemplateFormLabel title="Template Cover Image">
                        <Field
                          name="file"
                          render={({ field }: FieldProps<FormValues>) => (
                            <TemplateUploadFile
                              {...field}
                              initalImage={
                                template.asset && template.asset.urls.medium
                              }
                            />
                          )}
                        />
                        <TemplateFormLabel.Error name="file" />
                      </TemplateFormLabel>
                    </div>
                    <div className="template-col">
                      <TrimInputSpaces>
                        {!templateId && (
                          <TemplateFormLabel title="Chatbot to make template of">
                            <Field
                              component="select"
                              name="botId"
                              placeholder="Choose a bot you want to create a template from"
                            >
                              <option value="">Select bot</option>
                              {bots.map((bot: BasicChatbot) => (
                                <option key={bot.id} value={bot.id}>
                                  {bot.name}
                                </option>
                              ))}
                            </Field>
                            <TemplateFormLabel.Error name="botId" />
                          </TemplateFormLabel>
                        )}
                        <TemplateFormLabel title="Template name">
                          <Field
                            name="name"
                            placeholder="Give your new template a name"
                          />
                          <TemplateFormLabel.Error name="name" />
                        </TemplateFormLabel>
                        <TemplateFormLabel title="Template description">
                          <Field
                            name="description"
                            placeholder="Give awesome description"
                          />
                          <TemplateFormLabel.Error name="description" />
                        </TemplateFormLabel>
                        {templateId && (
                          <Link
                            className="template-col__edit-contents-link"
                            to={CHATBOT_HOME(templateId)}
                          >
                            Edit Template Contents
                          </Link>
                        )}
                      </TrimInputSpaces>
                    </div>
                  </div>

                  <TemplateModal.Footer>
                    <Button link onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        errors.name || errors.botId || errors.description
                      }
                      blue
                    >
                      {templateId ? 'Update' : 'Create'} template
                    </Button>
                  </TemplateModal.Footer>
                </Form>
              </fieldset>
            )}
          </Formik>
        </TemplateModal.Body>
      ) : (
        <TemplateModal.Body>
          <CreateOrExtendProgressBar
            isDone={!isProcessing}
            onClick={() => {
              setIsShowingCloneModal(false);
              onClose();
            }}
          />
        </TemplateModal.Body>
      )}
    </TemplateModal>
  );
};

const mapStateToProps = (state: StoreState) => ({
  bots: state.chatbot.basicListByCompany,
  company: activeCompanySelector(state),
  freeOrSpecialPricePlan:
    activeFacebookPagePlanInfoSelector(state).info.isFreeOrSpecialPlan,
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  createTemplate: (
    botId: number,
    name: string,
    description: string,
    assetId?: string,
    async?: boolean,
  ) =>
    dispatch(
      TemplatesCRUDActions.create(botId, name, description, assetId, async),
    ),
  updateTemplate: (
    templateId: number,
    name: string,
    description: string,
    assetId?: string,
  ) =>
    dispatch(
      TemplatesCRUDActions.update(templateId, name, description, assetId),
    ),
  getTemplateById: (templateId: number) =>
    dispatch(TemplatesCRUDActions.getById(templateId)),
  retrieveBasicListByCompany: (companyId: number) =>
    dispatch(chatbotCRUDActions.retrieveBasicListByCompany(companyId)),
  isStillCloning: async (templateId: number) => {
    const maybeTemplate = await dispatch(
      TemplatesCRUDActions.getById(templateId),
    );
    return maybeTemplate ? maybeTemplate.cloningProcess : true;
  },
});

const enhance = compose<InnerProps, OuterProps>(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
);

export const CreateTemplateFromBot = enhance(CreateTemplateFromBotPure);
